import { Flex, TableRowProps } from '@chakra-ui/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { CallOutcomeCell } from '~/pages/Calls/cells/CallOutcomeCell'
import { ContactCell } from '~/pages/Calls/cells/ContactCell'
import { ContactCombinedCell } from '~/pages/Calls/cells/ContactCombinedCell'
import { DateTimeCell } from '~/pages/Calls/cells/DateTimeCell'
import { DurationCell } from '~/pages/Calls/cells/DurationCell'
import { TranscriptCell } from '~/pages/Calls/cells/TranscriptCell'
import { useCallsListItemStyles } from '~/pages/Calls/componentStyles/CallsListItem.style'
import {
  CallTarget,
  getCallOutcomeData,
  getCallTargetData,
} from '~/pages/Calls/data/CallDetailData'
import { Size } from '~/types/ThemeTypes'
import { hasValue } from '~/utils/common'
import { formatSeconds } from '~/utils/formatter'

import { formatDateString, formatTimeString } from '@mangovoice/core'
import { Call, CallStatus, VoipProfileSubjectType } from '@mangovoice/types'

import { useCallTableStyles } from './componentStyles/CallsTable.style'
import { CallTableColumn } from './data/CallTableData'

interface CallLogsListItemProps extends TableRowProps {
  call: Call
  isSelected?: boolean
  tableSize: Size
  onSelected?: (callId: number) => void
  isFirst?: boolean
  onTranscriptClick?: () => void
}

export const CallListItem = ({
  call,
  isSelected,
  tableSize,
  isFirst,
  onSelected,
  onTranscriptClick,
}: CallLogsListItemProps) => {
  const { t } = useTranslation()
  const [isHovered, setIsHovered] = useState(false)

  const customerSentimentScore = call.call_sentiment?.customer_overall_score
  const isFromBot =
    call.from.voip_profile?.subject_type === VoipProfileSubjectType.Bot
  const isToBot =
    call.to.voip_profile?.subject_type === VoipProfileSubjectType.Bot

  const tableStyles = useCallTableStyles({ size: tableSize })
  const styles = useCallsListItemStyles({ size: tableSize })
  const startedAt = new Date(call.started_at)
  const startedDate = formatDateString(startedAt)
  const startedTime = formatTimeString(startedAt)
  const outcome = getCallOutcomeData(call)
  const hasVoicemail = hasValue(call.voicemail_id)
  const fromData = getCallTargetData(call, CallTarget.From)
  const toData = getCallTargetData(call, CallTarget.To)
  const durationString = formatSeconds(call.duration_in_seconds)
  const navigate = useNavigate()

  const handleVoicemailClick = () => {
    navigate(`${ApplicationRoutes.VOICEMAIL}/${call.voicemail_id}`)
  }

  const handleTranscriptClicked = () => {
    onTranscriptClick?.()
  }

  const handleSelected = () => {
    onSelected?.(call.id)
  }

  const getActionsLabel = (title: string, number: string) =>
    t('contactCell.menu.moreOptionsFor', {
      name: !title || title === number ? number : `${title} ${number}`,
      date: startedDate,
      time: startedTime,
    })

  return (
    <Flex
      role="row"
      css={styles.container}
      data-selected={isSelected ? '' : undefined}
      onClick={handleSelected}
      data-first={isFirst}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Flex css={styles.rowWrapper}>
        <DateTimeCell
          role="cell"
          columnStyles={tableStyles[CallTableColumn.DateTime]}
          date={startedDate}
          time={startedTime}
          onOpen={handleSelected}
          openLabel={t('callsTable.openCallDetails', {
            date: startedDate,
            time: startedTime,
          })}
          isCurrent={isSelected}
        />
        <CallOutcomeCell
          columnStyles={tableStyles[CallTableColumn.Outcome]}
          text={outcome.text}
          icon={outcome.icon}
          direction={call.direction}
          variant={outcome.variant}
          size={tableSize}
          onVoicemailClick={handleVoicemailClick}
        />
        {tableSize == Size.md || tableSize == Size.lg ? (
          <>
            <ContactCell
              role="cell"
              columnStyles={tableStyles[CallTableColumn.Contact]}
              title={fromData.titleValue ?? ''}
              subtitle={fromData.subtitleValue}
              contact={fromData.contact}
              number={fromData.number}
              isExtension={fromData.isExtension}
              isHovered={isHovered}
              isBotCall={isFromBot}
              actionsLabel={getActionsLabel(
                fromData.titleValue ?? '',
                fromData.number,
              )}
            />
            <ContactCell
              role="cell"
              columnStyles={tableStyles[CallTableColumn.Contact]}
              title={toData.titleValue ?? ''}
              subtitle={toData.subtitleValue}
              contact={toData.contact}
              number={toData.number}
              isExtension={toData.isExtension}
              isHovered={isHovered}
              isBotCall={isToBot}
              actionsLabel={getActionsLabel(
                toData.titleValue ?? '',
                toData.number,
              )}
            />
          </>
        ) : (
          <ContactCombinedCell
            columnStyles={tableStyles[CallTableColumn.Contact]}
            fromTitle={fromData.titleValue ?? ''}
            toTitle={toData.titleValue ?? ''}
            fromContact={fromData.contact}
            toContact={toData.contact}
            fromNumber={fromData.number}
            toNumber={toData.number}
            fromSubtitle={fromData.subtitleValue}
            toSubtitle={toData.subtitleValue}
            isFromExtension={fromData.isExtension}
            isToExtension={toData.isExtension}
            isFromBot={isFromBot}
            isToBot={isToBot}
          />
        )}
        <DurationCell
          columnStyles={tableStyles[CallTableColumn.Duration]}
          duration={durationString}
          hasVoicemail={hasVoicemail}
          pbxId={call.pbx.id}
          callId={call.id}
          transcriptId={call.transcript_id}
          customerSentimentScore={customerSentimentScore}
          onTranscriptClick={handleTranscriptClicked}
          onVoicemailClick={handleVoicemailClick}
          tableSize={tableSize}
          voicemailId={call.voicemail_id}
          isCallInProgress={call.status === CallStatus.InProgress}
        />
        {(tableSize == Size.lg || tableSize == Size.md) && (
          <TranscriptCell
            columnStyles={tableStyles[CallTableColumn.Transcript]}
            hasVoicemail={hasVoicemail}
            callId={call.id}
            pbxId={call.pbx.id}
            transcriptId={call.transcript_id}
            customerSentimentScore={customerSentimentScore}
            onTranscriptClick={handleTranscriptClicked}
            onVoicemailClick={handleVoicemailClick}
            voicemailId={call.voicemail_id}
            transcriptionStatus={call.transcription_status}
          />
        )}
      </Flex>
    </Flex>
  )
}
